import { CanSponsorViewStudyDocumentsGuard } from './can-sponsor-view-study-documents-guard';
import { SiteDocumentsComponent } from './documents/site-documents.component';
import { Routes } from '@angular/router';
import { SponsorGuard } from './sponsor-guard';
import { WorkspaceLevelLayoutComponent } from '../layout';
import { SponsorNavigationComponent } from './navigation/sponsor-navigation.component';
import { SelectedStudyResolver } from './selected-study-resolver';
import { SponsorCommunicationsComponent } from './documents/sponsor-communications.component';
import { StudyDocumentsComponent } from './documents/study-documents.component';
import { DocumentsByChildComponent } from './documents/documents-by-child.component';
import { SponsorLevelComponent } from './sponsor-level.component';
import { SponsorProtocolAccessReportComponent } from './activity-reports/sponsor-protocol-access-reports.component';
import { SponsorActivityReportDocumentsComponent } from './activity-reports/sponsor-activity-report-documents.component';
import { SponsorActivityReportsComponent } from './activity-reports/sponsor-activity-reports.component';
import { SponsorResultAlertsComponent } from './result-alerts/sponsor-result-alerts.component';
import { SponsorTrendAnalysisComponent } from './lab-results/sponsor-trend-analysis.component';
import { SponsorTrendsComponent } from './lab-results/sponsor-trends.component';
import { StudySitesComponent } from './sites/study-sites.component';
import { sponsorSubjectsRoutes } from './subjects/sponsor-subjects.routes';
import { CanSponsorViewDocumentCategoryGuard } from './can-sponsor-view-document-category-guard';
import { CanSponsorViewActivityReportsGuard } from './can-sponsor-view-activity-reports-guard';
import { SponsorStudyPermissionsResolver } from './sponsor-study-permissions-resolver';
import { CanSponsorViewProtocolAccessGuard } from './can-sponsor-view-protocol-access-guard';
import { SponsorTestResultsComponent } from './lab-results/sponsor-test-results.component';
import { SponsorStandardTestResultsComponent } from './lab-results/sponsor-standard-test-results.component';
import { SponsorMicrobiologyComponent } from './lab-results/sponsor-microbiology.component';
import { PageTitle } from '../core/page-title';
import { SiteDocumentsGridComponent } from './documents/site-documents-grid.component';
import { navigationActivity } from '../activities/navigation-activity/navigation-activity-resolver';
import { WorkspaceDrawerBehaviorResolver } from '../layout/workspace-drawer-behavior-resolver';
import { sponsorActivityReportsActivityHandler } from '../activities/navigation-activity/handlers/sponsor/sponsor-activity-reports-activity-handler';
import { sponsorDefaultActivityHandler } from '../activities/navigation-activity/handlers/sponsor/sponsor-default-activity-handler';
import { sponsorSiteDocumentsActivityHandler } from '../activities/navigation-activity/handlers/sponsor/sponsor-site-documents-activity-handler';
import { sponsorTestResultsActivityHandler } from '../activities/navigation-activity/handlers/sponsor/sponsor-test-results-activity-handler';
import { sponsorStudyDocumentsActivityHandler } from '../activities/navigation-activity/handlers/sponsor/sponsor-study-documents-activity-handler';
import { SponsorStudySiteIdentitiesResolver } from './lab-results/sponsor-study-site-identities-resolver';
import { SponsorSelectedStudySiteIdentityResolver } from './lab-results/sponsor-selected-study-site-identity-resolver';
import { SponsorTrendAnalysisRedirectionComponent } from './lab-results/sponsor-trend-analysis-redirection.component';
import { SiteDocumentsRedirectionComponent } from './documents/site-documents-redirection.component';
import { SponsorTestResultsRedirectionComponent } from './lab-results/sponsor-test-results-redirection.component';
import { routeData, routeParams } from '../shared/route-helpers';
import { LabTestResult } from '../lab-test-results';
import { SponsorBoxplotComponent } from './lab-results/sponsor-boxplot.component';
import { boxPlotVisitTickRenderrer, boxPlotSiteTickRenderrer } from '../charts/chart-helper';
import { SponsorActivityReportsFiltersGuard } from './activity-reports/sponsor-activity-reports-filters-guard';
import { Settings } from '../core';
import { SponsorLabReportsComponent } from './documents/lab-reports/sponsor-lab-reports.component';
import { SponsorKitInventoryComponent } from './kit-inventory/sponsor-kit-inventory.component';
import { SponsorKitInventoryOnHandComponent } from './kit-inventory/sponsor-kit-inventory-onhand.component';
import { SponsorKitInventoryExpirationComponent } from './kit-inventory/sponsor-kit-inventory-expiration.component';
import { CanSponsorViewKitInventoryGuard } from './can-sponsor-view-kit-inventory-guard';
import { SponsorKitInventoryFiltersGuard } from './kit-inventory/sponsor-kit-inventory-filters-guard';
import { SponsorEQueryTabsComponent } from './e-queries/sponsor-e-query-tabs/sponsor-e-query-tabs.component';
import { SponsorEQueryRedirectionComponent } from './e-queries/sponsor-e-query-tabs/sposnor-e-query-redirection.component';
import { CanSponsorViewEQueriesGuard } from './e-queries/can-sponsor-view-e-queries-guard';
import { SponsorAwaitingEQueriesComponent } from './e-queries/awaiting/sponsor-awaiting-e-queries.component';
import { SponsorSubmittedEQueriesComponent } from './e-queries/submitted/sponsor-submitted-e-queries.component';
import { defaultActivityHandler } from '../activities/navigation-activity/handlers/default-activity-handler';
import { TableauComponent } from './tableau/tableau.component';
import { CanSponsorViewStudyHealthDashboardGuard } from './can-sponsor-view-study-health-dashboard';
import { SponsorEReqReportComponent } from './activity-reports/eReq-reports/sponsor-ereq-reports.component';

export const sponsorRoutes: Routes = [
    {   
        path: `sponsor/study/:${routeParams.protocolId}`,
        canActivate: [SponsorGuard],
        resolve: {
            [routeData.selectedStudy]: SelectedStudyResolver,
            [routeData.studyPermissions]: SponsorStudyPermissionsResolver,
            [routeData.drawerBehavior]: WorkspaceDrawerBehaviorResolver,
        },
        component: SponsorLevelComponent,
        children: [
            {
                path: '',
                component: WorkspaceLevelLayoutComponent,
                children: [
                    {
                        path: 'sites',
                        pathMatch: 'full',
                        data: {
                            title: PageTitle.getSponsorTitle('Sites'),
                            ...sponsorDefaultActivityHandler('View Sites'),
                        },
                        resolve: { ...navigationActivity },
                        component: StudySitesComponent
                    },
                    {
                        path: 'sponsor-communications',
                        pathMatch: 'full',
                        canActivate: [CanSponsorViewDocumentCategoryGuard],
                        data: {
                            title: PageTitle.getSponsorTitle('Communications'),
                            ...sponsorDefaultActivityHandler('View Sponsor Communications'),
                        },
                        resolve: { ...navigationActivity },
                        component: SponsorCommunicationsComponent
                    },
                    {
                        path: 'result-alerts',
                        pathMatch: 'full',
                        data: {
                            title: PageTitle.getSponsorTitle('Result Alerts'),
                            ...sponsorDefaultActivityHandler('View Result Alerts'),
                        },
                        resolve: { ...navigationActivity },
                        component: SponsorResultAlertsComponent
                    },
                    {
                        path: 'lab-reports',
                        pathMatch: 'full',
                        data: {
                            title: PageTitle.getSponsorTitle('Lab Reports'),
                            ...sponsorDefaultActivityHandler('View Lab Reports'),
                        },
                        component: SponsorLabReportsComponent
                    },
                    {
                        path: `study-documents`,
                        data: {
                            title: PageTitle.getSponsorTitle('Study Documents'),
                            ...sponsorStudyDocumentsActivityHandler(),
                        },
                        resolve: { ...navigationActivity },
                        component: StudyDocumentsComponent,
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                redirectTo: 'study-manuals'
                            },
                            {
                                path: `:${routeParams.childCategoryId}`,
                                data: {
                                    title: PageTitle.getSponsorTitle('Study Documents'),
                                    ...sponsorStudyDocumentsActivityHandler(),
                                },
                                resolve: { ...navigationActivity },
                                canActivate: [CanSponsorViewStudyDocumentsGuard], 
                                component: DocumentsByChildComponent
                            }
                            
                        ]
                    },
                    {
                        path: 'site-documents',
                        resolve: {
                            [routeData.studySiteIdentities]: SponsorStudySiteIdentitiesResolver
                        },
                        children: [
                            {
                                path: '',
                                component: SiteDocumentsRedirectionComponent,
                            },
                            {
                                path: `:${routeParams.siteId}/:${routeParams.childCategoryId}`,
                                data: {
                                    title: PageTitle.getSponsorTitle('Site Documents'),
                                    ...sponsorSiteDocumentsActivityHandler(),
                                },
                                resolve: { ...navigationActivity },
                                component: SiteDocumentsComponent,
                                canActivate: [CanSponsorViewDocumentCategoryGuard],
                                children: [
                                    {
                                        path: '',
                                        component: SiteDocumentsGridComponent
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'trend-analysis',
                        resolve: {
                            [routeData.studySiteIdentities]: SponsorStudySiteIdentitiesResolver
                        },
                        children: [
                            {
                                path: '',
                                component: SponsorTrendAnalysisRedirectionComponent,
                            },
                            {
                                path: `:${routeParams.zcTestCode}`,
                                component: SponsorTrendAnalysisComponent,
                                children: [
                                    {
                                        path: 'site-boxplot',
                                        data: {
                                            ...sponsorDefaultActivityHandler('View Trend Analysis - Site BoxPlot'),
                                            [routeData.boxPlotGroupSelector]: (t: LabTestResult) => t.siteNumber,
                                            [routeData.boxPlotXAxisTitle]: 'Site',
                                            [routeData.boxPlotXTickRenderrer]: boxPlotSiteTickRenderrer,
                                            [routeData.boxPlotDataExtraFilter]: (r: LabTestResult) => r,
                                            title: PageTitle.getSponsorTitle('Site BoxPlot'),
                                        },
                                        resolve: { ...navigationActivity },
                                        component: SponsorBoxplotComponent
                                    },
                                    {
                                        path: 'visit-boxplot',
                                        data: {
                                            ...sponsorDefaultActivityHandler('View Trend Analysis - Visit BoxPlot'),
                                            [routeData.boxPlotGroupSelector]: (t: LabTestResult) => t.visitCode,
                                            [routeData.boxPlotXAxisSortingSelector]: (data: LabTestResult[]) => {
                                                return Math.min(...data
                                                    .filter(d => d.visitCollectionDateValue)
                                                    .map(d => d.visitCollectionDateValue));
                                            },
                                            [routeData.boxPlotXAxisTitle]: 'Scheduled Visits',
                                            [routeData.boxPlotXTickRenderrer]: boxPlotVisitTickRenderrer,
                                            [routeData.boxPlotDataExtraFilter]: (r: LabTestResult) => r.visitDescription,
                                            title: PageTitle.getSponsorTitle('Visit BoxPlot'),
                                        },
                                        resolve: { ...navigationActivity },
                                        component: SponsorBoxplotComponent
                                    },
                                    {
                                        path: 'trends',
                                        data: {
                                            ...sponsorDefaultActivityHandler('View Trend Analysis - Trends'),
                                            title: PageTitle.getSponsorTitle('Trends'),
                                        },
                                        resolve: { ...navigationActivity },
                                        component: SponsorTrendsComponent
                                    },
                                    {
                                        path: '',
                                        redirectTo: 'site-boxplot',
                                        pathMatch: 'full'
                                    }
                                ]
                            }
                        ]
                    },
                    {
                        path: 'test-results',
                        resolve: {
                            [routeData.studySiteIdentities]: SponsorStudySiteIdentitiesResolver
                        },
                        children: [
                            {
                                path: '',
                                component: SponsorTestResultsRedirectionComponent,
                            },
                            {
                                path: `:${routeParams.siteId}`,
                                resolve: {
                                    [routeData.selectedSiteIdentity]: SponsorSelectedStudySiteIdentityResolver
                                },
                                component: SponsorTestResultsComponent,
                                children: [
                                    {
                                        path: 'standard-test-results', 
                                        data: {
                                            title: PageTitle.getSponsorTitle('Standard Test Results'),
                                            ...sponsorTestResultsActivityHandler('View Standard Test Results'),
                                        },
                                        resolve: { ...navigationActivity },
                                        component: SponsorStandardTestResultsComponent
                                    },
                                    {
                                        path: 'microbiology',
                                        data: {
                                            title: PageTitle.getSponsorTitle('Microbiology'),
                                            ...sponsorTestResultsActivityHandler('View Microbiology Data'),
                                        },
                                        resolve: { ...navigationActivity },
                                        component: SponsorMicrobiologyComponent
                                    },
                                    {
                                        path: '',
                                        redirectTo: 'standard-test-results',
                                        pathMatch: 'full'
                                    }
                                ]
                            }
                        ]
                    },
                    ...sponsorSubjectsRoutes,
                    {
                        path: 'activity-reports',
                        data: { title: PageTitle.getSponsorTitle('Activity Reports') },
                        component: SponsorActivityReportsComponent,
                        children: [
                            {
                                path: 'documents',
                                children: [
                                    {
                                        path: '',
                                        redirectTo: 'equery',
                                        pathMatch: 'full'
                                    },
                                    {
                                        path: `:${routeParams.categoryId}`,
                                        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
                                        data: {
                                            ...sponsorActivityReportsActivityHandler(),
                                            [routeData.filterDefaultDateRange]: (s: Settings) => s.documentsFilterDefaultDateRange,
                                        },
                                        resolve: { ...navigationActivity },
                                        canActivate: [CanSponsorViewActivityReportsGuard, SponsorActivityReportsFiltersGuard],
                                        component: SponsorActivityReportDocumentsComponent
                                    }
                                ]
                            },
                            {
                                path: 'protocol-access',
                                data: {
                                    title: PageTitle.getSponsorTitle('Protocol Access'),
                                    ...sponsorDefaultActivityHandler('View Protocol Access'),
                                },
                                resolve: { ...navigationActivity },
                                canActivate: [CanSponsorViewProtocolAccessGuard],
                                component: SponsorProtocolAccessReportComponent
                            },
                            {
                                path: 'eReq-report',
                                data: {
                                    title: PageTitle.getSponsorTitle('eRequisition'),
                                    ...sponsorDefaultActivityHandler('View eReq Reports'),
                                },
                                resolve: { ...navigationActivity },
                                component: SponsorEReqReportComponent
                            },
                            {
                                path: '',
                                redirectTo: 'documents',
                                pathMatch: 'full'
                            }
                        ]
                    },
                    {
                        path: `kit-inventory`,
                        data: {
                            title: PageTitle.getSponsorTitle('Kit Inventory'),
                            ...sponsorDefaultActivityHandler('View Kit Inventory Report'),
                        },
                        resolve: { ...navigationActivity },
                        component: SponsorKitInventoryComponent,
                        children: [
                            {
                                path: 'inventory-summary',
                                data: {
                                    title: PageTitle.getSponsorTitle('Inventory Summary Report'),
                                    ...sponsorDefaultActivityHandler('View Kit Inventory Report'),
                                },
                                resolve: { ...navigationActivity },
                                canActivate: [CanSponsorViewKitInventoryGuard],
                                component: SponsorKitInventoryOnHandComponent
                            },
                            {
                                path: 'on-hand-expiration',
                                data: {
                                    title: PageTitle.getSponsorTitle('On Hand Inventory with Expiration Dates Report'),
                                    ...sponsorDefaultActivityHandler('View Kit Expiration Report'),
                                },
                                resolve: { ...navigationActivity },
                                canActivate: [CanSponsorViewKitInventoryGuard, SponsorKitInventoryFiltersGuard],
                                component: SponsorKitInventoryExpirationComponent
                            },
                            {
                                path: '',
                                redirectTo: 'inventory-summary',
                                pathMatch: 'full'
                            }
                        ]
                    },
                    {
                        path: 'tableau',
                        pathMatch: 'full',
                        data: {
                            title: PageTitle.getSponsorTitle('Study Health Dashboard'),
                            ...sponsorDefaultActivityHandler('View Study Health Dashboard'),
                        },
                        resolve: { ...navigationActivity },
                        canActivate: [CanSponsorViewStudyHealthDashboardGuard],
                        component: TableauComponent
                    },
                    {
                        path: '',
                        redirectTo: 'sites',
                        pathMatch: 'full'
                    },
                    {
                        path: '',
                        component: SponsorNavigationComponent,
                        outlet: 'navigation'
                    },

                    {
                        path: 'e-queries',
                        data: {
                            title: PageTitle.getSponsorTitle('eQueries')
                        },
                        resolve: {
                            [routeData.studySiteIdentities]: SponsorStudySiteIdentitiesResolver
                        },
                        canActivate: [CanSponsorViewEQueriesGuard],
                        children: [
                            {
                                path: '',
                                component: SponsorEQueryRedirectionComponent,
                            },
                            {
                                path: `:${routeParams.siteId}`,
                                resolve: {
                                    [routeData.selectedSiteIdentity]: SponsorSelectedStudySiteIdentityResolver,
                                },
                                component: SponsorEQueryTabsComponent,
                                children: [
                                    {
                                        path: 'awaiting',
                                        component: SponsorAwaitingEQueriesComponent,
                                    },
                                    {
                                        path: 'submitted',
                                        component: SponsorSubmittedEQueriesComponent
                                    },
                                    {
                                        path: '',
                                        redirectTo: 'awaiting',
                                        pathMatch: 'full'
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    }
];
